import React from 'react';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText
} from '@mui/material';
import { badValuesArray } from '../../ServiceEvents/utils';
import { trakrefBlue, inputGrey, Grey4, red, Grey1 } from '../../../color_utils';
import { trakrefFontFamily } from '../../../utils';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PropTypes from 'prop-types';

const getMarginTop = (variant) => {
    const marginTop = { standard: "0.8vh", filled: "1.8vh", outlined: "0vh" };
    return marginTop[variant];
};

const MultiSelectOption = ({
    id,
    disabled,
    required,
    error,
    value,
    label,
    options,
    onChange,
    width,
    variant,
    menuProps,
    sx,
    showNullOption,
    disabledMenuItems,
    disableUnderline,
    customHelperText,
    isFilterObject,
    renderSeparator,
    renderAllOption,
    allOptionLabel,
}) => {
    const allOption = { id: "__all__", name: allOptionLabel };
    const allOptionSelected = value.length === options.length;
    const valueSelector = (option) => {
        if (isFilterObject) {
            return option;
        }
        return option.value ? option.value : option.id;
    };
    const determineIcon = () => {
        if (error) {
            return [ErrorOutlineIcon, red];
        }
        if (disabled) {
            return [KeyboardArrowDownIcon, 'rgba(0, 0, 0, 0.26)'];
        }
        return [KeyboardArrowDownIcon, trakrefBlue];
    };

    const determineLabelStyle = () => {
        let styling = {
            "&.Mui-focused": { color: trakrefBlue },
            fontFamily: trakrefFontFamily
        };

        if (variant === "standard") {
            styling = {
                ...styling,
                "&.MuiFormLabel-root.MuiInputLabel-root": { left: "-13px" }
            };
        }
        return styling;
    };

    const determineHelperTextStyle = () => {
        let styling = {};

        if (variant === "standard") {
            styling = { "&.MuiFormHelperText-root": { marginLeft: 0 } };
        }
        return styling;
    };
    
    const handleOnChange = (e) => {
        const { value } = e.target;
        if (value.includes(allOption.id)) {
            if (allOptionSelected) {
                return onChange({ target: { value: [] } });    
            }
            return onChange({ target: { value: options.map(o => valueSelector(o)) } });
        }
        
        return onChange(e);
    };
    
    const getRenderValue = (selected) => {
        if (renderAllOption && allOptionSelected) return "All";
        return options.filter(x => selected.includes(valueSelector(x))).map(x => x.name).join(renderSeparator);
    };

    const labelStyle = determineLabelStyle();
    const helperTextStyle = determineHelperTextStyle();
    const [SelectIcon, selectIconColor] = determineIcon();

    const defaultStlyes = {
        '& .MuiSelect-select': {
            fontFamily: trakrefFontFamily,
            '&:focus': { backgroundColor: 'transparent' }
        },
        '& .MuiSelect-filled': { backgroundColor: inputGrey },
        '& .MuiSelect-filled.Mui-disabled': { backgroundColor: Grey4 },
        '& .MuiSvgIcon-root ': { fill: selectIconColor },
        '& .MuiFormLabel-root': { color: trakrefBlue, fontFamily: trakrefFontFamily },
        '& .MuiInputLabel-root': { color: trakrefBlue, fontFamily: trakrefFontFamily },
        '& .MuiInputBase-input': { textAlign: 'left' },
        '&:after': { borderColor: trakrefBlue },
    };

    return (
        <FormControl
            sx={{
                width: `${width}%`,
                '& .MuiFormLabel-root.MuiInputLabel-shrink': {
                    textTransform: "uppercase",
                    marginTop: getMarginTop(variant),
                    left: variant.toLowerCase() === "standard" ? "-14px" : "-2px",
                    color: disabled ? 'rgba(0, 0, 0, 0.38)' : trakrefBlue,
                    fontFamily: trakrefFontFamily,
                },
                "& .MuiFormLabel-root.MuiInputLabel-root.Mui-error": {
                    color: "#D51A1A !important",
                },
                "& .MuiInputBase-root.MuiInput-underline.Mui-error:after": {
                    borderBottom: "2px solid #D51A1A !important",
                },
                "& .MuiButtonBase-root-MuiMenuItem-root.Mui-selected": {
                    backgroundColor: "transparent !important"
                }
            }}
            required={required}
            error={error}
            disabled={disabled}
        >
            <InputLabel shrink={!badValuesArray.includes(value)} sx={labelStyle}>
                {label}
            </InputLabel>
            <Select
                multiple
                id={id}
                disableUnderline={disableUnderline}
                value={value}
                label={label}
                onChange={handleOnChange}
                notched={!badValuesArray.includes(value)}
                variant={variant}
                MenuProps={{
                    sx: {
                        "&& .Mui-selected": {
                            backgroundColor: "transparent",
                            color: Grey1
                        },
                    },
                    ...menuProps
                }}
                IconComponent={SelectIcon}
                renderValue={getRenderValue}
                sx={{ ...defaultStlyes, ...sx }}
            >
                {renderAllOption && (
                    <MenuItem
                        key={id}
                        value={allOption.id}
                        id={id}
                        disabled={disabled}
                        sx={{
                            padding: "0px 50px 0px 12px",
                        }}
                    >
                        <Checkbox
                            checked={allOptionSelected}
                            labelStyle={{ fontSize: "14px" }}
                            style={{ color: trakrefBlue }}
                        />
                        <ListItemText primary={allOption.name} />
                    </MenuItem>
                )}
                {options.map(option => {
                    const labelName = option.label ? option.label : (option.name ? option.name : option);
                    const valueSelected = valueSelector(option);
                    const disabled = disabledMenuItems.includes(valueSelected) || disabledMenuItems.some(item => JSON.stringify(item) === JSON.stringify(valueSelected));
                    const isChecked = value.length > 0 && (
                        value.includes(valueSelected) || 
                        value.some(item => JSON.stringify(item) === JSON.stringify(valueSelected))
                    );

                    return (
                        <MenuItem
                            testing_id={`${id}_${(option.id ? option.id : option)}`}
                            key={valueSelected}
                            value={valueSelected}
                            id={valueSelected}
                            disabled={disabled}
                            sx={{
                                padding: "0px 50px 0px 12px",
                            }}
                        >
                            <Checkbox
                                checked={isChecked}
                                labelStyle={{ fontSize: "14px" }}
                                style={{ color: trakrefBlue }}
                            />
                            <ListItemText primary={labelName} />
                        </MenuItem>
                    )
                })}
            </Select>
            {error && customHelperText === false && (
                <FormHelperText sx={helperTextStyle}>
                    This field is required
                </FormHelperText>
            )}
            {error && customHelperText !== false && (
                <FormHelperText sx={helperTextStyle}>
                    {customHelperText}
                </FormHelperText>
            )}
        </FormControl>
    );
};

MultiSelectOption.propTypes = {
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    width: PropTypes.number,
    variant: PropTypes.string,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    menuProps: PropTypes.object,
    sx: PropTypes.object,
    showNullOption: PropTypes.bool,
    disabledMenuItems: PropTypes.array,
    disableUnderline: PropTypes.bool,
    id: PropTypes.string,
    multiple: PropTypes.bool,
    renderSeparator: PropTypes.string,
    renderAllOption: PropTypes.bool,
    allOptionLabel: PropTypes.string,
};

MultiSelectOption.defaultProps = {
    width: 100,
    variant: "outlined",
    required: false,
    disabled: false,
    error: false,
    menuProps: {},
    sx: {},
    showNullOption: true,
    disabledMenuItems: [],
    disableUnderline: false,
    customHelperText: false,
    id: "",
    renderSeparator: ", ",
    renderAllOption: false,
    allOptionLabel: "All"
};

export default MultiSelectOption;